
import { ref, computed, reactive, watch, nextTick } from 'vue'
import { defineStore } from 'pinia'
import type { CostCenter } from './types'
import { faker } from '@faker-js/faker'
import axios from 'axios'
import { useAppStore } from './app'
import { useTableStore } from './table'
import { usePayslipStore } from './payslip'
import { useUtil } from '@/composables/util'

const apiURL: string = import.meta.env.VITE_API_URL + '/api'
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})


export const useCostStore = defineStore('cost', () => {
  const appStore = useAppStore()
  const tableStore = useTableStore()

  const costCenters = reactive(Array<CostCenter>())
  const selectedCostCenter = reactive({} as CostCenter) // selected in table
  // const thisCostCenter = reactive({} as CostCenter) // edit in form


  const thisCostCenter = reactive({
  // uuid: '',
  code: '',
  description: '',
  shift: 'rotation',

  daily_rate: 0.00,
  days_in_year: 314.0,
  hours_in_day: 8.0,
  basic_salary: 0.00,
  night_premium: 0.00,
  thirtenth_month: 0.00,
  sil: 0.00,

  uniform_allowance: 0.00,
  supervisor_allowance: 0.00,
  overtime: 0.00,
  // SUB TOTAL B

  ss_premium: 0.00,
  ss_provident_fund: 0.00,
  ss_ecc: 0.00,

  philhealth_premium: 0.00,
  pagibig_fund: 0.00,
  retirement_benefit: 0.00,
  // SUB TOTAL C

  // TOTAL REIMBURSABLE COST = B +
  total_reimbursable_cost: 0.00,
  administrative_cost: 0.00,
  total_before_taxes: 0.00,
  vat: 0.00,

  total_contract_rate: 0.00,
  subtotal_a: 0.00,
  subtotal_b: 0.00,
  subtotal_c: 0.00,

  } as CostCenter) //for form editing


  const showForm = ref(false)

  const my = useUtil()

  async function generateCostCenters(count = 10) {
    if (costCenters.length>10) {
      return
    }
    for (let index = 0; index < count; index++) {
    const costCenter = {} as CostCenter

    costCenter.uuid = faker.string.uuid()
    costCenter.code = costCenter.uuid.split('-')[0]
    costCenter.description = faker.company.name()

    costCenter.days_in_year = faker.number.float({ min: 300, max: 394, precision: 1 })
    costCenter.daily_rate = faker.number.float({ min: 300, max: 700, precision: 2 })

    // SUB TOTAL A
    costCenter.basic_salary = (costCenter.daily_rate * costCenter.days_in_year) / 12
    costCenter.thirtenth_month = (costCenter.daily_rate * 365) / 12 / 12
    costCenter.sil = (costCenter.daily_rate * 5) / 12
    costCenter.night_premium = (costCenter.basic_salary / 3) * 0.1
    costCenter.subtotal_a =
      costCenter.basic_salary +
      costCenter.night_premium +
      costCenter.thirtenth_month +
      costCenter.sil

    // SUB TOTAL B
    costCenter.uniform_allowance = faker.number.int(10) < 4 ? 100.0 : 0.0
    costCenter.supervisor_allowance = 0
    costCenter.overtime =
      ((costCenter.daily_rate / 8) * (125 / 100) * 4 * costCenter.days_in_year) / 12
    costCenter.subtotal_b =
      costCenter.uniform_allowance + costCenter.supervisor_allowance + costCenter.overtime

    // SUB TOTAL C
    costCenter.ss_premium = faker.number.float({ min: 100, max: 2000, precision: 2 })
    costCenter.ss_provident_fund = faker.number.float({ min: 100, max: 200, precision: 2 })
    costCenter.philhealth_premium =
      (costCenter.basic_salary * (4 / 100) * costCenter.days_in_year) / 12
    costCenter.ss_ecc = faker.number.float({ min: 100, max: 250, precision: 2 })
    costCenter.pagibig_fund = 100.00
    costCenter.retirement_benefit = (costCenter.daily_rate * 22.5) / 12
    costCenter.subtotal_c =
      costCenter.ss_premium +
      costCenter.ss_provident_fund +
      costCenter.philhealth_premium +
      costCenter.ss_ecc +
      costCenter.pagibig_fund +
      costCenter.retirement_benefit

      costCenter.total_reimbursable_cost = costCenter.subtotal_b + costCenter.subtotal_c
   costCenter.administrative_cost = costCenter.total_reimbursable_cost * 0.20
   costCenter.total_before_taxes = costCenter.total_reimbursable_cost + costCenter.administrative_cost
   costCenter.vat = costCenter.administrative_cost * 0.12

    costCenter.total_contract_rate = costCenter.total_before_taxes + costCenter.vat


      await addCostCenter(costCenter)
    }
  }

  async function saveCostCenter(payload: CostCenter) {
    if (payload.uuid) {
      const response = await api.put('/costs/' + payload.uuid, payload)

      if (response.status == 200) {
        if (response.data.errors) {
          appStore.errors = response.data.errors
          showForm.value = true
        } else {
          const idx = costCenters.findIndex((item: CostCenter)=>item.uuid == payload.uuid)
          costCenters.splice(idx,1,response.data)
          appStore.errors = {}
          showForm.value = false

          // recalc payslips using this cost center in Open periods
          usePayslipStore().dirtyAffectedPayslips({ cost_uuid: payload.uuid })
        }
      } else {
        appStore.errors = response.data
      }
    } else {
      const response = await api.post('/costs.json', payload)

      if (response.status == 200) {
        if ('errors' in response.data) {
          appStore.errors = response.data.errors
          showForm.value = true
        } else {
          costCenters.push(response.data)
          appStore.errors = {}
          showForm.value = false
        }
      } else {
      }
    }
  }

  async function addCostCenter(payload: CostCenter) {


    const response = (await api.post('/costs.json', payload))

    if (response.status == 200) {
      if ('errors' in response.data) {
        appStore.errors = response.data.errors
        showForm.value = true
      } else {
        costCenters.push(response.data)
        appStore.errors = {}
        showForm.value = false
      }
    } else {
    }
    
  }

  async function deleteCostCenter(payload: CostCenter) {
    const response = await api.delete("/costs/" + payload.uuid);
    // record was actually deleted
    if (response.status == 200) {
      if (response.data.deleted>0) {
        const ndx = costCenters.findIndex(
          (item: CostCenter) => item.uuid == payload.uuid
        );       
        costCenters.splice(ndx,1)
        appStore.errors = {}
        showForm.value = false

        // recalc payslips that referenced this cost center in Open periods
        usePayslipStore().dirtyAffectedPayslips({ cost_uuid: payload.uuid })
      } else {
        appStore.errors = response.data.errors
        showForm.value = true
      }
    }
  }

  async function getCostCenters() {

    const response = (await api.get('/costs.json'))
    if (response.status == 200) {
      // employees.push(response.data.data)
      if ('errors' in response.data) {
        appStore.errors = response.data.errors
      } else {
        costCenters.length=0
        response.data.data.forEach((item: CostCenter)=>{
          costCenters.push(item)
        })
  
      }

    } else {
    }
  }

  const importCostCenters = async (costCenters: any) => {
    for (let index = 0; index < costCenters.length; index++) {
      if (costCenters[index]['uuid']=='uuid') {
        continue
      }
      
      // const {uuid,...costCenter} = costCenters[index]
      const costCenter = costCenters[index]

      const payload = {} as CostCenter

      Object.assign(payload,costCenter)
      await saveCostCenter(payload)
    }
  }


  const costCenterName = computed(()=>(uuid: string) => {
    return costCenters.find((item: CostCenter)=> item.uuid == uuid)?.code

  })

  const costCenterUuid = computed(()=>(code: string) => {
    return costCenters.find((item: CostCenter)=> item.code.toLocaleLowerCase() == code.toLocaleLowerCase())?.uuid
  })


  const costCode = computed(()=>(uuid: string)=>{
    return costCenters.find((item: CostCenter)=>item.uuid == uuid)?.code

  })


  watch(thisCostCenter, async (newCostCenter: CostCenter, oldCostCenter: CostCenter | undefined)=>{
    const cc = thisCostCenter as any
    const nc = newCostCenter as any

    cc.basic_salary = my.formatMoney(my.parseMoney(nc.daily_rate) * my.parseMoney(nc.days_in_year) / 12)
    // Night differential = basic_salary * night_premium_factor (payFactor.NP,
    // 0.10) * a shift multiplier: day → 0, night → 1 (all hours in the window),
    // rotation → 1/3 (one shift in three lands at night).
    cc.night_premium = my.formatMoney(
      my.parseMoney(nc.basic_salary) * appStore.payFactor.NP * my.nightShiftFactor(nc.shift)
    )

    // Thirteemth = use days_in_year_13 inputted different from regular days_in_year
    const diy = my.parseMoney(nc.days_in_year_13) > 0 ? my.parseMoney(nc.days_in_year_13) : my.parseMoney(nc.days_in_year)
    cc.thirtenth_month = my.formatMoney(my.parseMoney(nc.daily_rate) * (diy / 12 / 12))
    cc.sil = my.formatMoney(my.parseMoney(nc.daily_rate) * 5 / 12)
    cc.overtime = my.formatMoney((my.parseMoney(nc.daily_rate) / 8) * (nc.hours_duty - nc.hours_in_day) * nc.overtime_factor * nc.days_in_year / 12)

    cc.retirement_benefit = my.formatMoney(my.parseMoney(nc.daily_rate) * 22.5 / 12)
    cc.subtotal_a = my.formatMoney(my.parseMoney(nc.basic_salary) + my.parseMoney(nc.night_premium)
      + my.parseMoney(nc.thirtenth_month) + my.parseMoney(nc.sil))

    cc.subtotal_b = my.formatMoney(my.parseMoney(nc.uniform_allowance) + my.parseMoney(nc.supervisor_allowance)
      + my.parseMoney(nc.overtime))

    // SS, PhilHealth, Pagibig
    const net_salary = my.parseMoney(nc.basic_salary) - my.parseMoney(nc.thirtenth_month)
    const ss_salary_credit = my.parseMoney(cc.subtotal_a) + my.parseMoney(cc.subtotal_b) - my.parseMoney(nc.thirtenth_month)

    // SS = Basic Salary - Thirteenth Month then lookup table
    const SS = tableStore.SS.sort((item: any) => item.From).find(
      (range: any) =>
        parseFloat(range.From.replace(',', '')) < ss_salary_credit &&
        ss_salary_credit <= parseFloat(range.To.replace(',', ''))
    )
    if (SS != undefined) {
      cc.ss_premium = my.formatMoney(my.parseMoney(SS.ER_SS))
      cc.ss_ecc = my.formatMoney(SS.ER_EC)
      cc.ss_provident_fund = my.formatMoney(SS.ER_MPF)
    }

    // PhilHealth = Basic Salary then lookup table, the result must be divided by two (split sharing)
    const PH = tableStore.PH.sort((item: any) => item.From).find(
      (range: any) =>
        parseFloat(range.From.replace(',', '')) < nc.basic_salary &&
        nc.basic_salary <= parseFloat(range.To.replace(',', ''))
    )
    if (PH != undefined) {
      cc.philhealth_premium = my.formatMoney((nc.basic_salary * PH.Rate / 100) / 2)
    }

    // Pagibig HDMF - Basic Salary to lookup table, then the employer share on the basic capped at the table's fund salary ceiling
    const PF = tableStore.PF.sort((item: any) => item.From).find(
      (range: any) =>
        parseFloat(range.From.replace(',', '')) < nc.basic_salary &&
        nc.basic_salary <= parseFloat(range.To.replace(',', ''))
    )
    if (PF != undefined) {
      cc.pagibig_fund = my.formatMoney(Math.min(nc.basic_salary, my.parseMoney(PF.Cap) || nc.basic_salary) * PF.ER / 100)
    }

    cc.subtotal_c = my.formatMoney(my.parseMoney(nc.ss_premium) + my.parseMoney(nc.ss_provident_fund)
      + my.parseMoney(nc.ss_ecc) + my.parseMoney(nc.philhealth_premium) + my.parseMoney(nc.pagibig_fund)
      + my.parseMoney(nc.retirement_benefit))

    cc.total_reimbursable_cost = my.formatMoney(my.parseMoney(nc.subtotal_a) + my.parseMoney(nc.subtotal_b) + my.parseMoney(nc.subtotal_c))
    cc.administrative_cost = my.formatMoney(my.parseMoney(nc.total_reimbursable_cost) * 0.20)

    cc.total_before_taxes = my.formatMoney(my.parseMoney(nc.total_reimbursable_cost) + my.parseMoney(nc.administrative_cost))
    cc.vat = my.formatMoney(my.parseMoney(nc.administrative_cost) * 0.12)
    cc.total_contract_rate = my.formatMoney(my.parseMoney(nc.total_before_taxes) + my.parseMoney(nc.vat))


  },{immediate: true})

  // const basic_salary = computed(()=>{
  //     const result = my.formatMoney(my.parseMoney(thisCostCenter.daily_rate ) * my.parseMoney(thisCostCenter.days_in_year) / 12)
  //     thisCostCenter.basic_salary = result
  //     return result
  //   })

  const setThisCostCenter = (payload: CostCenter) => {
    Object.assign(thisCostCenter,payload)
  }

  return {
    showForm,
    costCenters,
    selectedCostCenter,
    thisCostCenter,

    // generateCostCenters,
    addCostCenter,
    saveCostCenter,
    deleteCostCenter,
    getCostCenters,
    importCostCenters,
    setThisCostCenter,

    costCenterName,
    costCenterUuid,
    costCode,

    // basic_salary,
  
  }
})
